import { Box, Icon, IconButton, Tooltip, useColorMode } from '@chakra-ui/react';
import { FiMoon, FiSun } from 'react-icons/fi';
import { useThemeMode } from '../../hooks/v0/useThemeMode';
import { useTranslation } from 'react-i18next';

interface ThemeToggleProps {
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
  className?: string;
}

/**
 * 主题切换按钮组件
 * 允许用户在亮色和暗色主题之间切换
 */
export const ThemeToggle = ({ 
  size = 'md',
  showTooltip = true,
  className = ''
}: ThemeToggleProps) => {
  const { colorMode, toggleColorMode } = useColorMode();
  const { systemTheme } = useThemeMode();
  const { t } = useTranslation(['v0/common']);
  
  // 按钮大小映射
  const btnSizes = {
    sm: { btn: 'sm', icon: '16px' },
    md: { btn: 'md', icon: '20px' },
    lg: { btn: 'lg', icon: '24px' },
  };
  
  // 确定图标和标签
  const icon = colorMode === 'dark' ? FiSun : FiMoon;
  const label = colorMode === 'dark' 
    ? t('themeToggle.switchToLight') 
    : t('themeToggle.switchToDark');
  
  // 状态标签（当前是系统主题还是手动选择）
  const statusLabel = colorMode === systemTheme
    ? (colorMode === 'dark' ? t('v0/themeToggle.currentDark') : t('themeToggle.currentLight'))
    : (colorMode === 'dark' ? t('v0/themeToggle.manualDark') : t('themeToggle.manualLight'));

  const button = (
    <IconButton
      aria-label={label}
      icon={<Icon as={icon} boxSize={btnSizes[size].icon} />}
      onClick={toggleColorMode}
      size={btnSizes[size].btn}
      variant="ghost"
      className={className}
      color={colorMode === 'dark' ? 'yellow.200' : 'purple.600'}
      _hover={{
        bg: colorMode === 'dark' ? 'whiteAlpha.200' : 'blackAlpha.100',
      }}
    />
  );
  
  // 根据showTooltip属性决定是否包装在Tooltip中
  if (showTooltip) {
    return (
      <Tooltip label={`${label} (${statusLabel})`} placement="bottom">
        {button}
      </Tooltip>
    );
  }
  
  return button;
}; 